// In-memory log buffer with a size cap. Used as the fallback when the IndexedDB log outbox is not available
// (private mode, quota): the oldest records are dropped first and the gap is reported once as a
// `logs.dropped` event, so it is visible in Grafana instead of silently missing.
// Records have the outbox shape: { source, username, ts, event }.

export const LOGS_DROPPED_EVENT = 'logs.dropped';

export function createLogsDroppedEvent({ count, from, to, reason, now = Date.now() }) {
    const range = from && to ? ` between ${new Date(from).toISOString()} and ${new Date(to).toISOString()}` : '';
    return {
        level: 'warn',
        event: LOGS_DROPPED_EVENT,
        payload: { 0: `dropped ${count} log events${range} (reason: ${reason})`, timestamp: String(now) },
    };
}

export class BoundedLogBuffer {
    #records = [];
    #maxRecords;
    #dropped = null;
    #takenReport = null;

    constructor({ maxRecords = 5000 } = {}) {
        this.#maxRecords = maxRecords;
    }

    get size() {
        return this.#records.length;
    }

    push(...records) {
        this.#records.push(...records);
        this.#trim();
    }

    // Takes up to maxRecords oldest records; a pending drop report goes first so it is sent with them.
    take(maxRecords = this.#records.length) {
        const batch = this.#records.splice(0, maxRecords);
        if (!this.#dropped) return batch;

        const { count, from, to, source, username } = this.#dropped;
        const now = Date.now();
        const report = { source, username, ts: now, event: createLogsDroppedEvent({ count, from, to, reason: 'buffer full', now }) };
        this.#takenReport = { report, dropped: this.#dropped };
        this.#dropped = null;
        return [report, ...batch];
    }

    // Puts a batch that could not be sent back in front, keeping the original order. A drop report taken with
    // it goes back to the pending counters instead, so it is neither duplicated nor lost.
    requeue(batch) {
        const taken = this.#takenReport;
        this.#takenReport = null;
        const records = taken ? batch.filter((record) => record !== taken.report) : batch;
        if (taken && records.length !== batch.length) this.#dropped = this.#merge(taken.dropped, this.#dropped);
        this.#records.unshift(...records);
        this.#trim();
    }

    #trim() {
        const overflow = this.#records.length - this.#maxRecords;
        if (overflow <= 0) return;

        const dropped = this.#records.splice(0, overflow);
        const last = dropped.at(-1);
        this.#dropped = this.#merge(this.#dropped, {
            count: dropped.length,
            from: dropped[0].ts,
            to: last.ts,
            source: last.source,
            username: last.username,
        });
    }

    #merge(older, newer) {
        if (!older) return newer;
        if (!newer) return older;
        return { ...newer, count: older.count + newer.count, from: older.from ?? newer.from };
    }
}
